import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { colorOf, COLOR_KEYS, fmtDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Upload, Trash2, BookOpen, Calculator, ChevronDown, Plus } from "lucide-react";

function GradeCalculator({ course }) {
  const [cats, setCats] = useState([]);
  const [target, setTarget] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const res = await api.get(`/grade-categories?course_id=${course.course_id}`);
      setCats(res.data); setLoading(false);
    })();
  }, [course.course_id]);

  const updateScore = async (cat, val) => {
    const score = val === "" ? null : Number(val);
    setCats(cats.map((c) => c.category_id === cat.category_id ? { ...c, current_score: score } : c));
    try { await api.patch(`/grade-categories/${cat.category_id}`, { current_score: score }); } catch (e) {}
  };

  if (loading) return <Loader2 className="h-4 w-4 animate-spin text-blue-600" />;
  if (cats.length === 0) return <p className="text-sm text-slate-500">No grading weights were found in this syllabus.</p>;

  const totalWeight = cats.reduce((s, c) => s + (c.weight || 0), 0);
  const scored = cats.filter((c) => c.current_score != null && c.current_score !== "");
  const earnedWeight = scored.reduce((s, c) => s + (c.weight || 0), 0);
  const current = earnedWeight > 0
    ? scored.reduce((s, c) => s + (c.current_score * c.weight), 0) / earnedWeight
    : null;

  const remainingWeight = totalWeight - earnedWeight;
  let needed = null;
  if (target && remainingWeight > 0 && current != null) {
    const currentContribution = scored.reduce((s, c) => s + (c.current_score * c.weight), 0) / 100;
    needed = ((Number(target) - currentContribution) / (remainingWeight / 100));
  } else if (target && current != null && remainingWeight === 0) {
    needed = 0;
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        {cats.map((c) => (
          <div key={c.category_id} className="flex items-center gap-3">
            <span className="flex-1 text-sm text-slate-700">{c.name} <span className="text-slate-400">({c.weight}%)</span></span>
            <div className="flex items-center gap-1">
              <Input type="number" min="0" max="100" value={c.current_score ?? ""} placeholder="—"
                onChange={(e) => updateScore(c, e.target.value)} data-testid={`grade-score-${c.category_id}`}
                className="w-20 h-8 text-sm" />
              <span className="text-sm text-slate-400">%</span>
            </div>
          </div>
        ))}
      </div>
      {current != null && (
        <div className="rounded-lg bg-slate-50 border border-slate-100 p-3">
          <p className="text-sm text-slate-600">Current grade (based on entered scores): <span className="font-semibold text-slate-900">{current.toFixed(1)}%</span></p>
        </div>
      )}
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <label className="text-xs text-slate-500">Target final grade</label>
          <Input type="number" value={target} onChange={(e) => setTarget(e.target.value)} placeholder="90"
            data-testid={`grade-target-${course.course_id}`} className="h-9 mt-1" />
        </div>
      </div>
      {needed != null && (
        <p className="text-sm text-slate-700" data-testid="grade-result">
          {remainingWeight === 0
            ? "All graded work is entered — no remaining work to calculate."
            : needed > 100
            ? `You'd need about ${needed.toFixed(0)}% on remaining work — that may not be reachable for a ${target}% target.`
            : needed < 0
            ? `You've already secured your ${target}% target.`
            : `You need approximately ${needed.toFixed(0)}% on the remaining work to finish with a ${target}%.`}
        </p>
      )}
      <p className="text-xs text-slate-400">Calculations depend on the grading structure provided by your syllabus.</p>
    </div>
  );
}

export default function CoursesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [gradeFor, setGradeFor] = useState(null);
  const [delTarget, setDelTarget] = useState(null);

  const load = async () => {
    const [c, e] = await Promise.all([api.get("/courses"), api.get("/events")]);
    setCourses(c.data); setEvents(e.data); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const del = async () => {
    try {
      await api.delete(`/courses/${delTarget.course_id}`);
      toast.success("Course deleted");
      setDelTarget(null); load();
    } catch (e) { toast.error("Could not delete course"); }
  };

  const eventsFor = (id) => events.filter((e) => e.course_id === id);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900">Courses</h1>
        <Button onClick={() => navigate("/upload")} data-testid="courses-add-btn" className="bg-slate-900 hover:bg-slate-800 rounded-lg">
          <Plus className="h-4 w-4 mr-1" /> Add course
        </Button>
      </div>

      {courses.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white p-10 text-center" data-testid="courses-empty">
          <p className="text-slate-600 mb-4">No courses yet.</p>
          <Button onClick={() => navigate("/upload")} className="bg-blue-600 hover:bg-blue-700 rounded-lg"><Upload className="h-4 w-4 mr-1" /> Upload a Syllabus</Button>
        </div>
      ) : (
        <div className="space-y-3">
          {courses.map((c) => {
            const col = colorOf(c.color);
            const evs = eventsFor(c.course_id);
            const open = expanded === c.course_id;
            return (
              <div key={c.course_id} data-testid={`course-${c.course_id}`} className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                <div className="flex items-center gap-3 p-5">
                  <span className={`inline-flex h-10 w-10 items-center justify-center rounded-lg ${col.bg} ${col.text}`}><BookOpen className="h-5 w-5" /></span>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-900">{c.code ? `${c.code} — ` : ""}{c.name}</p>
                    <p className="text-xs text-slate-500">{[c.instructor, c.semester].filter(Boolean).join(" · ") || "—"} · {evs.length} events</p>
                  </div>
                  <button onClick={() => setGradeFor(c)} data-testid={`grades-btn-${c.course_id}`} className="text-slate-400 hover:text-blue-600 p-2" title="Grade calculator"><Calculator className="h-4 w-4" /></button>
                  <button onClick={() => setDelTarget(c)} data-testid={`delete-course-${c.course_id}`} className="text-slate-400 hover:text-red-600 p-2"><Trash2 className="h-4 w-4" /></button>
                  <button onClick={() => setExpanded(open ? null : c.course_id)} data-testid={`expand-${c.course_id}`} className="text-slate-400 hover:text-slate-700 p-2">
                    <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
                  </button>
                </div>
                {open && (
                  <div className="border-t border-slate-100 divide-y divide-slate-50">
                    {evs.length === 0 ? <p className="p-4 text-sm text-slate-400">No events.</p> : evs.map((e) => (
                      <div key={e.event_id} className="flex items-center justify-between px-5 py-2.5">
                        <span className="text-sm text-slate-700">{e.title} <span className="text-xs text-slate-400 capitalize">· {e.type}</span></span>
                        <span className="text-xs text-slate-500">{fmtDate(e.date)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!user?.paid_status && courses.length >= 1 && (
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-5 flex items-center justify-between gap-4" data-testid="courses-upgrade-banner">
          <p className="text-sm text-blue-900">Free accounts are limited to one course. Unlock unlimited courses for a one-time $4.99.</p>
          <Button onClick={() => navigate("/upgrade")} className="bg-blue-600 hover:bg-blue-700 rounded-lg shrink-0">Unlock</Button>
        </div>
      )}

      <Dialog open={!!gradeFor} onOpenChange={(o) => !o && setGradeFor(null)}>
        <DialogContent data-testid="grade-calc-modal">
          <DialogHeader><DialogTitle>Grade calculator — {gradeFor?.name}</DialogTitle></DialogHeader>
          {gradeFor && <GradeCalculator course={gradeFor} />}
        </DialogContent>
      </Dialog>

      <Dialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <DialogContent data-testid="delete-course-modal">
          <DialogHeader><DialogTitle>Delete this course?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">This will permanently remove "{delTarget?.name}" and all its events and grades.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelTarget(null)}>Cancel</Button>
            <Button onClick={del} data-testid="confirm-delete-course" className="bg-red-600 hover:bg-red-700 text-white">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
